/**
 * My Approvals Tab Component
 * Shows overdue, pending, and approved items for current user
 */
import React, { useState } from 'react';
import { 
  AlertTriangle, 
  Clock, 
  CheckCircle2,
  Filter,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import ApprovalCard from './ApprovalCard';

export default function MyApprovalsTab({ 
  queue, 
  onApprove, 
  onReject, 
  onViewEvidence,
  onBulkApprove,
  selectedIds,
  onSelectItem,
  onSelectAll,
  isLoading
}) {
  const [expandedSections, setExpandedSections] = useState({
    overdue: true,
    pending: true,
    approved: false
  });
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  const overdueItems = queue?.overdue || [];
  const pendingItems = queue?.pending || [];
  const total = queue?.total || 0;
  
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 animate-pulse">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#2F2F2F] rounded-lg" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-[#2F2F2F] rounded w-1/3" />
                <div className="h-3 bg-[#2F2F2F] rounded w-1/2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  if (total === 0) {
    return (
      <div className="text-center py-16 bg-[#111111] border border-[#1F1F1F] rounded-xl">
        <CheckCircle2 size={48} className="mx-auto text-emerald-500/30 mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">All Caught Up!</h3>
        <p className="text-sm text-[#6B6B6B]">No items are pending your approval.</p>
      </div>
    );
  }
  
  // Calculate if all visible items are selected
  const allVisibleIds = [...overdueItems, ...pendingItems].map(item => item.id);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => selectedIds?.includes(id));
  const someSelected = selectedIds?.length > 0;
  
  return (
    <div className="space-y-6">
      {/* Bulk Actions Bar */}
      {someSelected && (
        <div className="sticky top-0 z-10 bg-[#111111] border border-emerald-500/30 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => onSelectAll && onSelectAll(allVisibleIds)}
              className="w-4 h-4 rounded border-[#3F3F3F] bg-[#1F1F1F] text-emerald-500"
            />
            <span className="text-sm text-white">
              {selectedIds?.length} item{selectedIds?.length > 1 ? 's' : ''} selected
            </span>
          </div>
          <button
            onClick={() => onBulkApprove && onBulkApprove(selectedIds)}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
            data-testid="bulk-approve-btn"
          >
            <CheckCircle2 size={14} />
            Bulk Approve ({selectedIds?.length})
          </button>
        </div>
      )}
      
      {/* Overdue Section */}
      {overdueItems.length > 0 && (
        <Section
          title="Overdue"
          count={overdueItems.length}
          icon={AlertTriangle}
          iconColor="text-red-400"
          bgColor="bg-red-500/10"
          borderColor="border-red-500/30"
          isExpanded={expandedSections.overdue}
          onToggle={() => toggleSection('overdue')}
        >
          <div className="space-y-3">
            {overdueItems.map(item => (
              <ApprovalCard
                key={item.id}
                item={item}
                onApprove={onApprove}
                onReject={onReject}
                onViewEvidence={onViewEvidence}
                isSelected={selectedIds?.includes(item.id)}
                onSelect={onSelectItem}
              />
            ))}
          </div>
        </Section>
      )}
      
      {/* Pending Section */}
      {pendingItems.length > 0 && (
        <Section
          title="Pending"
          count={pendingItems.length}
          icon={Clock}
          iconColor="text-amber-400"
          bgColor="bg-amber-500/10"
          borderColor="border-amber-500/30"
          isExpanded={expandedSections.pending}
          onToggle={() => toggleSection('pending')}
        >
          <div className="space-y-3">
            {pendingItems.map(item => (
              <ApprovalCard
                key={item.id}
                item={item}
                onApprove={onApprove}
                onReject={onReject}
                onViewEvidence={onViewEvidence}
                isSelected={selectedIds?.includes(item.id)}
                onSelect={onSelectItem}
              />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ 
  title, 
  count, 
  icon: Icon, 
  iconColor, 
  bgColor, 
  borderColor, 
  isExpanded, 
  onToggle, 
  children 
}) {
  return (
    <div className={`rounded-xl border ${borderColor} overflow-hidden`}>
      <button
        onClick={onToggle}
        className={`w-full px-4 py-3 ${bgColor} flex items-center justify-between hover:opacity-90 transition-opacity`}
      >
        <div className="flex items-center gap-2">
          <Icon size={16} className={iconColor} />
          <span className="text-sm font-medium text-white">{title}</span>
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${bgColor} ${iconColor}`}>
            {count}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp size={16} className="text-[#6B6B6B]" />
        ) : (
          <ChevronDown size={16} className="text-[#6B6B6B]" />
        )}
      </button>
      
      {isExpanded && (
        <div className="p-4 bg-[#0A0A0A]">
          {children}
        </div>
      )}
    </div>
  );
}
